/**
 * The phone's map sheet: where it can rest and where a drag ends. Pure, so it can be tested.
 *
 * The sheet is drawn at its natural height (`sheetH`, capped by the screen) and moved down with a transform; an
 * offset of 0 is fully open. Rest points:
 *  - open: the whole sheet (the mockup layout, the default);
 *  - half: only for a tall sheet — half the screen shows;
 *  - peek: the header and the pinned footer (the main button) — the map gets the rest of the screen.
 */
export type SnapName = 'open' | 'half' | 'peek';
export type SnapPoint = { name: SnapName; offset: number };

/** Below this speed (px/ms) a release goes to the nearest rest point; above it the sheet flings to the next one. */
export const FLING = 0.45;

export function snapPoints(sheetH: number, viewH: number, peekVisible: number): SnapPoint[] {
  const points: SnapPoint[] = [{ name: 'open', offset: 0 }];
  const peek = Math.max(0, Math.round(sheetH - Math.min(sheetH, peekVisible)));
  const halfVisible = Math.round(viewH * 0.5);
  // a half stop only makes sense when it sits clearly between open and peek
  if (sheetH - halfVisible > 80 && halfVisible - peekVisible > 80) points.push({ name: 'half', offset: sheetH - halfVisible });
  if (peek > 24) points.push({ name: 'peek', offset: peek });
  return points;
}

/** Where a drag released at `offset` with `velocity` (px/ms, positive = moving down) comes to rest. */
export function settle(offset: number, velocity: number, points: SnapPoint[]): SnapPoint {
  const sorted = [...points].sort((a, b) => a.offset - b.offset);
  if (Math.abs(velocity) >= FLING) {
    if (velocity > 0) return sorted.find((p) => p.offset > offset + 1) ?? sorted[sorted.length - 1];
    return [...sorted].reverse().find((p) => p.offset < offset - 1) ?? sorted[0];
  }
  return sorted.reduce((best, p) => (Math.abs(p.offset - offset) < Math.abs(best.offset - offset) ? p : best), sorted[0]);
}

/** Dragging past the ends gives way less and less (rubber band), so the sheet never jumps off its edges. */
export function rubber(offset: number, min: number, max: number): number {
  if (offset < min) return min - Math.sqrt(min - offset) * 4;
  if (offset > max) return max + Math.sqrt(offset - max) * 4;
  return offset;
}

/** Release speed from the last pointer samples (px/ms), over at most the last 100 ms. */
export function velocityOf(samples: { y: number; t: number }[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  let first = samples[samples.length - 2];
  for (let i = samples.length - 2; i >= 0; i--) {
    if (last.t - samples[i].t > 100) break;
    first = samples[i];
  }
  const dt = last.t - first.t;
  return dt > 0 ? (last.y - first.y) / dt : 0;
}
